import type { PlayerProfileDetailRow } from '@/components/player-profile-detail';
import { STRINGS } from '@/constants/strings';
import { auth } from '@/features/auth/types';
import { calculateAge, formatGuardianApiDate } from '@/features/guardian-player/utils';
import type { GuardianPlayer } from '@/features/guardian/services/get-guardian-players';
import { countryNameToCode } from '@/features/onboarding/utils/country-helpers';
import { levelColor } from '@/features/onboarding/utils/player-card';
import { formatPlayerGender } from '@/utils/player-gender';

/** Reuse the co-guardian detail sheet copy so both "profile detail" cards read identically. */
const strings = STRINGS.guardianPlayer.coGuardianProfile.detailSheet;

/** Capitalizes the first letter; API casing is inconsistent (dominantHand is lowercase). */
const capitalize = (value?: string): string | undefined => {
  const trimmed = value?.trim();
  if (!trimmed) return undefined;
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
};

/** City, State, Country (ISO), comma-joined; `undefined` when all parts are empty. */
const addressLabel = (address?: auth.Address): string | undefined => {
  // Country is stored as the full name; show its ISO code (e.g. "US") like the design.
  const country = countryNameToCode(address?.country) || address?.country?.trim();
  const parts = [address?.city, address?.state, country]
    .map(part => part?.trim())
    .filter((part): part is string => Boolean(part));
  return parts.length > 0 ? parts.join(', ') : undefined;
};

export type ViewPlayerDetail = {
  avatarUrl?: string;
  name: string;
  level: number;
  levelBadgeColor: string;
  ageLabel: string | null;
  rows: PlayerProfileDetailRow[];
};

/** Maps a saved server player into the read-only "View player" summary-card view model. */
export function buildViewPlayerDetail(player: GuardianPlayer): ViewPlayerDetail {
  const age = calculateAge(player.dob);
  const rows = (
    [
      { label: strings.birthdate, value: formatGuardianApiDate(player.dob, 'MMM d, yyyy') },
      { label: strings.gender, value: formatPlayerGender(player.gender) },
      { label: strings.height, value: player.height ? `${player.height}"` : undefined },
      { label: strings.dominantHand, value: capitalize(player.dominantHand) },
      { label: strings.address, value: addressLabel(player.address) },
      { label: strings.zipCode, value: player.address?.zipcode?.trim() || undefined },
    ] as { label: string; value?: string }[]
  ).filter((row): row is PlayerProfileDetailRow => Boolean(row.value));

  return {
    avatarUrl: player.avatarUrl,
    name: `${player.firstname ?? ''} ${player.lastname ?? ''}`.trim(),
    level: player.level,
    levelBadgeColor: levelColor(player.level),
    ageLabel:
      age !== null ? `${age} ${age === 1 ? strings.ageYearOld : strings.ageYearsOld}` : null,
    rows,
  };
}
